import { useEffect, useRef, useState } from 'react';
import { ReactNode } from 'react';
import { Flex, Layout, Row } from 'antd';
import { Header } from '@/components';
import { Sidebar } from '@/components/Sidebar';
import { WorkspaceSwitchOverlay } from '@/components/Workspace/WorkspaceSwitcher';
import { useTranslation } from 'react-i18next';
import { LOCAL_EN, ROUTE } from '@/config';
import useScrollToTop from '@/hooks/useScrollToTop';
import { CURRENT_RELEASE_NOTE } from '@/constants/releaseNote';
import { useReleaseNotesStore } from '@/stores/useReleaseNotesStore';
import { ReleaseNotesPopup } from '@/components/ReleaseNotesPopup';
import { NewTicketModal } from '@/components/Header/TicketPageHeader/NewTicketModal/NewTicketModal';
import { useTicketingStore } from '@/stores';
import usePermissionSync from '@/hooks/usePermissionSync';
import usePermission from '@/hooks/usePermission';
import { useUserStore } from '@/stores';
import NoWorkspaceLanding from '@/components/Workspace/NoWorkspaceLanding';
import PendingInvitationBanner from '@/components/Workspace/PendingInvitationBanner';
import { useNavigate, useLocation } from 'react-router-dom';

interface MainProps {
  children: ReactNode;
}

const Main = ({ children }: MainProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = usePermission();
  const user = useUserStore((state) => state.user);

  /*
   * 🔴 D14 — A MERCHANT WITH NO WORKSPACE GETS THE INVITATIONS SCREEN, NOT THE
   * DASHBOARD.
   *
   * Measured before this existed: an invited person's first login rendered the
   * full chrome (New Order, Orders, Finance, Claims) for an account belonging
   * to no workspace, with three company-scoped requests answering 403 behind a
   * half-broken profile form. They arrive here because of the redirect below —
   * no workspace means no roles means `permissions.length === 0` means
   * `/settings`.
   *
   * ⚠️ `undefined` IS NOT ZERO. `workspaces` is absent until /user resolves,
   * and treating "not loaded yet" as "has none" would flash this screen at
   * every merchant on every cold load. Only an ACTUAL empty array counts.
   */
  const hasNoWorkspace = Array.isArray(user?.workspaces) && user.workspaces.length === 0;

  useScrollToTop();
  usePermissionSync(); // Sync permissions on route changes

  const seenVersion = useReleaseNotesStore((state) => state.seenVersion);
  const setSeenVersion = useReleaseNotesStore((state) => state.setSeenVersion);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const { isModalOpen } = useTicketingStore();

  // Redirect to settings if user has no permissions
  useEffect(() => {
    // ⚠️ Not for the workspace-less: they are shown the invitations landing
    // below, and navigating them to /settings first produces exactly the
    // half-broken profile page D14 is about.
    if (hasNoWorkspace) {
      return;
    }

    if (permissions.length === 0 && location.pathname !== ROUTE.SETTINGS) {
      navigate(ROUTE.SETTINGS, { replace: true });
    }
  }, [permissions, location.pathname, navigate, hasNoWorkspace]);

  /*
   * The route that brought a workspace-less person here was chosen while they
   * had no roles, so it is `/settings` — `DefaultRedirect` asked
   * `getDefaultRoute(hasPermission)` and that is all an empty permission set
   * can answer. Accepting an invitation gives them roles but does not revisit
   * that decision, so they landed in the app on Settings › Profile.
   *
   * Sending them to ROUTE.HOME re-runs `DefaultRedirect` against the
   * permissions they now have. It fires only on the true→false edge, so a
   * merchant who already had a workspace is never moved off the page they are
   * on — accepting a second invitation JOINS, it does not switch.
   */
  const wasWorkspaceless = useRef(hasNoWorkspace);
  useEffect(() => {
    if (wasWorkspaceless.current && !hasNoWorkspace) {
      navigate(ROUTE.HOME, { replace: true });
    }
    wasWorkspaceless.current = hasNoWorkspace;
  }, [hasNoWorkspace, navigate]);

  useEffect(() => {
    if (CURRENT_RELEASE_NOTE.version && CURRENT_RELEASE_NOTE.version !== seenVersion) {
      setShowReleaseNotes(true);
    }
  }, [seenVersion]);

  const handleCloseReleaseNotes = () => {
    setShowReleaseNotes(false);
    setSeenVersion(CURRENT_RELEASE_NOTE.version);
  };

  /*
   * ⭐⭐⭐ RETURNED INSTEAD OF `children`, WHICH IS THE POINT. Rendering the page
   * and hiding its furniture still MOUNTS the page, and the page is what fires
   * the company-scoped requests. Returning here means those components never
   * mount, so the 403s cannot happen — nothing asks, rather than something
   * filtering afterwards.
   */
  if (hasNoWorkspace) {
    return <NoWorkspaceLanding />;
  }

  return (
    <>
      <ReleaseNotesPopup
        visible={showReleaseNotes}
        note={CURRENT_RELEASE_NOTE.note}
        onClose={handleCloseReleaseNotes}
      />
      <Layout className='h-screen bg-background overflow-hidden' style={{}}>
        <Flex className='w-full h-full' align='flex-start'>
          <Sidebar />
          {/*
            `relative` so the workspace switch overlay can cover THE CONTENT
            AREA and nothing else. The sidebar deliberately stays visible during
            a switch — it already shows the target workspace, which is the most
            reassuring thing on the screen while the token is being swapped.
          */}
          <Flex vertical className='relative w-full flex-1 min-h-0 min-w-0 overflow-auto h-full'>
            <Header />
            <div className='px-4 sm:px-10 pt-6 pb-6 flex-1 flex flex-col min-h-0'>
              {/*
                For somebody who already HAS a workspace: a strip, not a
                takeover. The sidebar's badge counts invitations; a count is not
                an action, and the owner asked for Accept/Decline to be here.
              */}
              <PendingInvitationBanner />
              {children}
            </div>
            <WorkspaceSwitchOverlay />
          </Flex>
        </Flex>
        <NewTicketModal isOpen={isModalOpen} />
      </Layout>
    </>
  );
};

export default Main;
